"use client";
import {
  Button,
  makeStyles,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { useNavigate, useParams } from "react-router-dom";
import { ClientError } from "../models/ClientError";
import { ClientRoutes } from "../models/ClientRoutes";
import { useChapterGet, useUpdateChapter } from "../hooks/useChapter";
import ChapterForm from "../components/ChapterForm";
import { ChapterFormData, UpdateChapterRequest } from "../models/Chapter";
import { useTranslation } from "react-i18next";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalM,
  },
});

function EditChapterPage() {
  const classes = useClasses();
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!id) {
    const missingIdError: ClientError = {
      message: "Activity id is missing",
      name: "Activity id is missing",
    };

    throw missingIdError;
  }

  const idString = id as string;

  const { data: chapter, isLoading, isRefetching } = useChapterGet(idString);
  const { mutateAsync: updateChapter, isPending } = useUpdateChapter(idString);

  async function onFormSubmit(formData: ChapterFormData) {
    const updateRequest: UpdateChapterRequest = {
      id: idString,
      title: formData.title,
      collectionTitle: formData.collectionTitle?.id ?? "",
      KMACollaborationVariant: formData.KMACollaborationVariant?.id ?? null,
      rootNode: formData.rootNode?.id ?? "",
      imageId: formData.imageId,
      searchFilterConfiguration: formData.searchFilterConfiguration,
      chapterNavigationConfig: formData.chapterNavigationConfig,
    };

    await updateChapter(updateRequest);
  }

  return (
    <div className={classes.root}>
      <h1>{t("editChapter")}</h1>
      {!isLoading ? (
        <ChapterForm
          isLoading={isPending}
          existingChapter={chapter}
          onSubmit={onFormSubmit}
          isRefetching={isRefetching}
        />
      ) : (
        <Spinner />
      )}
      <div className={classes.buttons}>
        <Button onClick={() => navigate(ClientRoutes.Chapters)}>
          {t("back")}
        </Button>
      </div>
    </div>
  );
}

export default EditChapterPage;
